// Website pixels: the Organization defaults drawer. The defaults prefill new
// pixels only; the field rules and the copy explaining that live in
// lib/pixel-draft.ts beside DEFAULTS_FIELDS.

import { useState } from 'react';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { pixelDefaults } from '@/api/resources';
import type { PixelDefaults, PixelDefaultsUpsert } from '@/api/types';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { queryClient } from '@/cache/queryClient';
import { MutationErrorLine } from '@/components/error-panel';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { DEFAULTS_FIELDS, defaultsFieldError, defaultsUpsertBody } from '@/lib/pixel-draft';
import { fmtRelative } from './admin-user-drawer';

const PIXEL_DEFAULTS_KEY = pixelDefaults.all;

export interface OrgDefaultsDrawerProps {
  defaults: PixelDefaults | undefined;
  onClose: () => void;
}

export function OrgDefaultsDrawer({ defaults, onClose }: OrgDefaultsDrawerProps) {
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(DEFAULTS_FIELDS.map(f => [f.key, defaults?.[f.key] ?? ''])));
  const required = useRequiredFeedback();
  const [attempted, setAttempted] = useState(false);
  const set = (key: string, value: string) => { setDraft(d => ({ ...d, [key]: value })); required.clear(`org-default-${key}`); };

  const saveM = useRegisteredMutation({
    domains: ['pixels'],
    mutationFn: (body: PixelDefaultsUpsert) => api.put<PixelDefaults>('/pixels/defaults', { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PIXEL_DEFAULTS_KEY });
      onClose();
    },
  });

  const errors = DEFAULTS_FIELDS.map(f => ({ key: f.key, message: defaultsFieldError(f.key, draft[f.key]) }))
    .filter((e): e is { key: typeof e.key; message: string } => !!e.message);
  // Advisory only — mirrors the server's `ttd_incomplete()`, which warns and
  // still saves so an operator can fill the triple in over two sittings.
  const adv = (draft.ttd_adv_id ?? '').trim();
  const tag = ((draft.ttd_ct_id ?? '').trim() || (draft.ttd_ct_id_pageview ?? '').trim());
  const ttdIncomplete = !!adv !== !!tag;
  const isEmpty = DEFAULTS_FIELDS.every(f => !(draft[f.key] ?? '').trim());

  const submit = () => {
    if (saveM.isPending) return;
    const valid = required.report(errors.map(e => ({ key: `org-default-${e.key}`, message: e.message })));
    if (!valid) { setAttempted(true); return; }
    saveM.mutate(defaultsUpsertBody(draft));
  };

  return (
    <>

      <ResponsiveOverlay label="pixels details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{flex:1, minWidth:0}}>
            <div className="muted mono" style={{fontSize:10,letterSpacing:'.12em',textTransform:'uppercase'}}>Website Pixels</div>
            <h2 style={{fontFamily:'var(--font-display)',fontSize:20,fontWeight:800,letterSpacing:'.01em',margin:'4px 0 6px'}}>Organization defaults</h2>
            <div className="muted" style={{fontSize:11.5,lineHeight:1.45}}>
              These <b>prefill new pixels only</b>. Saving does not modify any pixel that already exists.
              Provider secrets are never stored here — only identifiers.
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18,padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-body">
          {DEFAULTS_FIELDS.map(f => (
            <RequiredField
              key={f.key}
              fieldKey={`org-default-${f.key}`}
              message={required.messages[`org-default-${f.key}`]}
              attempt={required.attempt}
              className="field"
              style={{marginBottom:12}}
            >
              <label className="field-lbl"><span>{f.label}</span></label>
              <input className="input mono" style={{fontSize:12}} placeholder={f.placeholder}
                value={draft[f.key] ?? ''} onChange={e => set(f.key, e.target.value)}/>
              <div className="field-hint">{f.hint}</div>
            </RequiredField>
          ))}

          {ttdIncomplete && (
            <div className="mono" style={{fontSize:11, color:'var(--warn)', marginTop:4, lineHeight:1.4}}>
              ⚠ Incomplete Trade Desk default — an advertiser needs at least one tag ID (and vice
              versa) to prefill anything usable. This still saves.
            </div>
          )}
          {isEmpty && (
            <div className="muted" style={{fontSize:11, marginTop:8, lineHeight:1.4}}>
              Every field is blank — saving clears the organization defaults entirely.
            </div>
          )}
          {attempted && errors.length > 0 && (
            <div className="mono" style={{marginTop:12,fontSize:11.5,color:'var(--bad)'}}>
              ● Fix {errors.length} field{errors.length > 1 ? 's' : ''} before saving
            </div>
          )}
          <MutationErrorLine error={saveM.error}/>
          {defaults?.updated_at && (
            <div className="muted mono" style={{fontSize:10,marginTop:10,letterSpacing:'.06em'}}>
              Last updated {fmtRelative(+new Date(defaults.updated_at))}
            </div>
          )}
        </div>

        <div className="drawer-foot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saveM.isPending} onClick={submit}>
            {saveM.isPending ? 'Saving…' : isEmpty ? 'Clear defaults' : 'Save defaults'}
          </button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
